import { Link } from '@tanstack/react-router'
import { ListPlus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { initials } from '@/components/ui/avatar-initials'
import { Button } from '@/components/ui/button'
import { CreateTrackedItemDialog } from '@/features/tracker/CreateTrackedItemDialog'
import { formatDayLabel } from '@/lib/dates'
import { nextCallLabel } from '@/types/clientMeetings'
import { CADENCE_LABELS, memberName } from '@/types/clientDashboard'
import type { ClientDashboard } from '@/types/clientDashboard'
import { ChurnPill, PercentPill } from './HealthPills'
import { LogEmailDialog } from './LogEmailDialog'
import { PrepareMeetingLink } from './PrepareMeetingLink'
import { RenewalPill } from './RenewalPill'
import { clientTabPath } from './tabs'

export function teamNames(dashboard: ClientDashboard): string | null {
  const names = dashboard.accountTeam.map(memberName)
  if (!names.length) return null
  return names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

function Fact({ label, value, testId }: { label: string; value: string | null; testId?: string }) {
  return (
    <span data-testid={testId}>
      {label} {value ? <b className="font-medium text-etn-text">{value}</b> : <span className="italic text-etn-text-muted">Not set</span>}
    </span>
  )
}

function LinkedPill({ label, testId, to, children }: { label: string; testId: string; to: string; children: React.ReactNode }) {
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1.5">
      {label}
      <Link to={to as never} className="inline-flex">
        {children}
      </Link>
    </span>
  )
}

export function ClientDashboardHeader({ dashboard }: { dashboard: ClientDashboard }) {
  const summary = dashboard.kpiSummary
  return (
    <div className="mb-4">
      <div className="mb-3 text-[12.5px] text-etn-text-label">
        <Link to={'/clients' as never} className="font-medium text-etn-accent-text-active hover:underline">
          Client dashboards
        </Link>{' '}
        / {dashboard.companyName}
      </div>
      <div className="flex items-center gap-3.5 rounded-etn-card border border-etn-border-hairline bg-etn-surface px-5 py-[18px] shadow-etn-whisper">
        <span aria-hidden data-testid="company-initials" className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-etn-accent-tint text-[15px] font-bold text-etn-accent-text-active">
          {initials(dashboard.companyName, { oneWordLetters: 2 })}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-etn-text">{dashboard.companyName}</h1>
            {dashboard.companyDeleted && <Badge variant="destructive">Company deleted</Badge>}
          </div>
          <div className="mt-[5px] flex flex-wrap gap-x-3.5 gap-y-1 text-[12.5px] text-etn-text-label">
            <Fact label="Account team" value={teamNames(dashboard)} />
            <Fact label="Next call" value={nextCallLabel(dashboard.nextCallAt)} testId="next-call" />
            <Fact label="Last call" value={dashboard.lastCallAt ? formatDayLabel(dashboard.lastCallAt) : null} testId="last-call" />
            <Fact label="Cadence" value={dashboard.cadence ? CADENCE_LABELS[dashboard.cadence] : null} />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[12.5px] text-etn-text-label">
            <LinkedPill label="Health score" testId="health-score" to={clientTabPath(dashboard.companyId, 'kpis')}>
              <PercentPill
                percent={summary?.healthPercent ?? null}
                band={summary?.healthBand ?? null}
                title={summary ? `Based on ${summary.healthAvailable} of ${summary.healthTotal} KPIs.${summary.lastMonth != null ? ` Last month ${summary.lastMonth}%.` : ''}` : undefined}
              />
            </LinkedPill>
            <LinkedPill label="Churn risk" testId="churn-risk" to={clientTabPath(dashboard.companyId, 'kpis')}>
              <ChurnPill churn={summary?.churn ?? null} />
            </LinkedPill>
            <LinkedPill label="HelpDesk Health" testId="helpdesk-health" to={clientTabPath(dashboard.companyId, 'helpdesk-health')}>
              <PercentPill percent={summary?.helpDesk.percent ?? null} band={summary?.helpDesk.band ?? null} />
            </LinkedPill>
            <RenewalPill companyId={dashboard.companyId} showNone />
          </div>
        </div>
        <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2">
          <PrepareMeetingLink />
          {dashboard.clientTrackerEnabled && (
            <CreateTrackedItemDialog
              initialCompany={{ _id: dashboard.companyId, name: dashboard.companyName }}
              lockCompany
              trigger={
                <Button size="sm" variant="outline">
                  <ListPlus />
                  Raise tracker item
                </Button>
              }
            />
          )}
          <LogEmailDialog companyId={dashboard.companyId} companyName={dashboard.companyName} />
        </div>
      </div>
    </div>
  )
}
